import { CART_TYPE } from '@/services/cart/models/cart';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { useCart } from '@/hooks/useCart';
import { Box } from '@mui/material';
import { useNotification } from '@/hooks/useNotification';
import { dataLayerPushWithEvent, emitMoeEvent } from '@/utils/dataLayer';
import { ApiState, BaseAxiosApiError } from '@/models/generics';
import { useState } from 'react';
import LencoButton from '@/atomic-components/LencoButton';
import FlexBox from '@/atomic-components/FlexBox';
import { getColor } from '@/utils/theme/colors';
import OfferDiscountIcon from '@/assets/offers/OfferDiscountIcon.svg';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { NotificationSeverity } from '@/components/NotifyToast';
import CartOffers from '@/components/Cart/CartOffers';
import { useLogin } from '@/hooks/useLogin';
import { SelectedOffer } from '@/components/Cart/VerticalCart';

export default function OffersCart() {
	const { loader } = useFullScreenLoader();
	const {
		cart,
		cartActions,
		rentCart,
		buyCart,
		buyCartItemsCount,
		rentalCartItemsCount,
	} = useCart();
	const { actions } = useNotification();
	const { getCartAttributes } = useCart();
	const [offerBeingApplied, setOfferBeingApplied] = useState<SelectedOffer>();

	const [removeCouponApiState, setRemoveCouponApiState] = useState<ApiState>(
		ApiState.IDLE
	);
	const [offersListVisible, setOffersListVisible] = useState<boolean>(false);

	const [applyOfferErrorMessage, setApplyOfferErrorMessage] =
		useState<string>();
	const { isLoggedIn, loginActions } = useLogin();

	function emitOfferEvent(
		eventName: string,
		data: {
			buyTotal: number;
			rentTotal: number;
			total: number;
			code?: string;
			cart_Type: string;
		}
	) {
		const attributes = getCartAttributes();

		emitMoeEvent(eventName, {
			cart_type: data?.cart_Type,
			lineofproduct: attributes.lineOfProductsString,
			vertical: attributes.verticalsString,
			buy_products: attributes.buyProductIdsString,
			rent_products: attributes.rentalProductIdsString,
			total_cart_value: data?.total,
			buy_cart_value: data?.buyTotal,
			rent_cart_value: data?.rentTotal,
			buy_offer_config: buyCartItemsCount > 0 ? data?.code : '',
			rent_offer_config: rentalCartItemsCount > 0 ? data?.code : '',
		});
	}
	const DisplayAppliedCodeStatus = (props: {
		appliedCouponCodeError?: string;
	}) => {
		if (props.appliedCouponCodeError) {
			return (
				<LencoTypography
					ml='8px'
					color={getColor('Danger +1')}
					textTransform='capitalize'
					variant='Tiny_Medium'
				>
					{props.appliedCouponCodeError}
				</LencoTypography>
			);
		}

		return (
			<LencoTypography
				ml='8px'
				color={getColor('Gray -2')}
				textTransform='capitalize'
				variant='Tiny_Medium'
			>
				Applied
			</LencoTypography>
		);
	};

	const DisplayAppliedCardTitle = (props: {
		appliedCouponSavings?: string;
		appliedCouponCode?: string;
		appliedCouponCodeError?: string;
	}) => {
		return (
			<Box ml='8px'>
				<FlexBox alignItems={'center'}>
					<LencoTypography
						color={getColor('Gray 0')}
						textTransform='capitalize'
					>
						{props.appliedCouponCode}
					</LencoTypography>
					<DisplayAppliedCodeStatus
						appliedCouponCodeError={props.appliedCouponCodeError}
					/>
				</FlexBox>
				<LencoTypography variant='Small_Medium' color={getColor('Success +1')}>
					Saved {props.appliedCouponSavings}
				</LencoTypography>
			</Box>
		);
	};

	function publishOfferAppliedEvent(cartType: CART_TYPE) {
		const attr = getCartAttributes();
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: 'Offers and Discounts Applied',

			moe_event_data: {
				cart_type: cartType,
				vertical: attr?.verticalsString,
				lineofproduct: attr?.lineOfProductsString,
				total_cart_value: cart?.total?.value,
				buy_cart_value: cart?.buyCart?.total?.value,
				rent_cart_value: cart?.rentCart?.total?.value,
				rent_products: attr?.rentalProductIds?.length,
				buy_products: attr?.buyProductIds?.length,
			},
		});
	}
	async function applyOfferToCart(
		selectedOffer: SelectedOffer,
		cartType: CART_TYPE
	) {
		try {
			publishOfferAppliedEvent(cartType);
			if (!isLoggedIn) {
				loginActions.showLoginFlow();
			}

			if (selectedOffer.code?.length == 0) {
				setApplyOfferErrorMessage('Please enter a valid coupon code');
				return;
			}
			setApplyOfferErrorMessage('');
			setOfferBeingApplied(selectedOffer);

			loader.show();

			const res = await cartActions.applyCouponCode(cartType, {
				appliedCouponCode: selectedOffer.code,
				appliedCouponId: selectedOffer.id,
			});

			emitOfferEvent('Offers and Discounts Applied', {
				buyTotal: res.data?.data?.cart?.buyCart?.total.value,
				rentTotal: res.data?.data?.cart?.rentCart?.total.value,
				total: res.data?.data?.cart?.total?.value,
				code: selectedOffer.code,
				cart_Type: cartType,
			});

			if (res.data.success) {
				setOffersListVisible(false);
				actions.notifyUser({
					message: 'Offer applied successfully',
					severity: NotificationSeverity.SUCCESS,
				});
			} else {
				actions.notifyUser({
					message: 'Something went wrong',
					severity: NotificationSeverity.WARNING,
				});
			}
		} catch (error: any) {
			console.error(error);
			const errMessage =
				error?.response?.data?.error?.message ?? 'Something Went wrong';
			setApplyOfferErrorMessage(errMessage);

			actions.notifyUser({
				message: errMessage,
				severity: NotificationSeverity.ERROR,
			});
		}

		loader.hide();

		setOfferBeingApplied(undefined);
	}
	function publishOfferRemovedEvent(cartType: CART_TYPE) {
		const attr = getCartAttributes();
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: 'Offers and Discounts Removed',
			moe_event_data: {
				cart_type: cartType,
				vertical: attr?.verticalsString,
				lineofproduct: attr?.lineOfProductsString,
				total_cart_value: cart?.total?.value,
				buy_cart_value: cart?.buyCart?.total?.value,
				rent_cart_value: cart?.rentCart?.total?.value,
				rent_products: attr?.rentalProductIds?.length,
				buy_products: attr?.buyProductIds?.length,
			},
		});
	}
	async function removeCouponCode(e: any, cartType: CART_TYPE, code: any) {
		try {
			e.stopPropagation();
			loader.show();

			const res = await cartActions.removeCoupon(cartType);

			if (res.data.success) {
				setRemoveCouponApiState(ApiState.SUCCESS);
				publishOfferRemovedEvent(cartType);

				actions.notifyUser({
					message: 'Offer removed',
					severity: NotificationSeverity.SUCCESS,
				});
				return;
			} else {
				actions.notifyUser({
					message: res.data.error.message,
					severity: NotificationSeverity.WARNING,
				});
			}
		} catch (err) {
			const error = err as BaseAxiosApiError;
			console.error(error);
			setRemoveCouponApiState(ApiState.FAILED);
			actions.notifyUser({
				message: error?.response?.data.error?.message,
				severity: NotificationSeverity.ERROR,
			});
			return;
		} finally {
			loader.hide();
			setRemoveCouponApiState(ApiState.COMPLETED);
		}
	}
	return (
		<>
			{cart && rentalCartItemsCount > 0 && (
				<CartOffers
					onRemove={(e: any) =>
						removeCouponCode(
							e,
							CART_TYPE.RENT,
							rentCart?.appliedCoupon?.appliedCouponCode
						)
					}
					offers={cart?.rentCart?.offers}
					applyOfferToCart={applyOfferToCart}
					cartType={CART_TYPE.RENT}
					emitOfferEvent={emitOfferEvent}
				/>
			)}
			{cart && buyCartItemsCount > 0 && (
				<Box mt='24px'>
					<CartOffers
						onRemove={(e: any) =>
							removeCouponCode(
								e,
								CART_TYPE.BUY,
								buyCart?.appliedCoupon?.appliedCouponCode
							)
						}
						offers={cart?.buyCart?.offers}
						applyOfferToCart={applyOfferToCart}
						cartType={CART_TYPE.BUY}
						emitOfferEvent={emitOfferEvent}
					/>
				</Box>
			)}
			{rentCart?.appliedCoupon?.appliedCouponCode && (
				<FlexBox
					sx={{
						backgroundColor: getColor('Yellow -3'),
						p: '16px',
						borderRadius: '16px',
						mt: '16px',
						justifyContent: 'space-between',
					}}
				>
					<FlexBox alignItems='center'>
						<OfferDiscountIcon />
						<DisplayAppliedCardTitle
							appliedCouponCode={rentCart?.appliedCoupon?.appliedCouponCode}
							appliedCouponCodeError={
								rentCart?.appliedCoupon?.appliedCouponCodeError
							}
							appliedCouponSavings={
								rentCart?.appliedCoupon?.appliedCouponSavings?.displayValue
							}
						/>
					</FlexBox>
					<LencoButton
						variant='text'
						sx={{
							p: '0px',
							color: getColor('Gray -2'),
							textTransform: 'capitalize',
						}}
						onClick={(e) =>
							removeCouponCode(
								e,
								CART_TYPE.RENT,
								rentCart?.appliedCoupon?.appliedCouponCode
							)
						}
						isLoading={removeCouponApiState === ApiState.LOADING}
					>
						<FlexBox alignItems='center'>
							<LencoTypography variant='Small_Medium' mr='8px'>
								Remove
							</LencoTypography>
							x
						</FlexBox>
					</LencoButton>
				</FlexBox>
			)}
			{buyCart?.appliedCoupon?.appliedCouponCode && (
				<FlexBox
					sx={{
						backgroundColor: getColor('Yellow -3'),
						p: '16px',
						borderRadius: '16px',
						mt: '16px',
						justifyContent: 'space-between',
					}}
				>
					<FlexBox alignItems='center'>
						<OfferDiscountIcon />
						<DisplayAppliedCardTitle
							appliedCouponCode={buyCart?.appliedCoupon?.appliedCouponCode}
							appliedCouponCodeError={
								buyCart?.appliedCoupon?.appliedCouponCodeError
							}
							appliedCouponSavings={
								buyCart?.appliedCoupon?.appliedCouponSavings?.displayValue
							}
						/>
					</FlexBox>
					<LencoButton
						variant='text'
						sx={{
							p: '0px',
							color: getColor('Gray -2'),
							textTransform: 'capitalize',
						}}
						onClick={(e) =>
							removeCouponCode(
								e,
								CART_TYPE.BUY,
								buyCart?.appliedCoupon?.appliedCouponCode
							)
						}
						isLoading={removeCouponApiState === ApiState.LOADING}
					>
						<FlexBox alignItems='center'>
							<LencoTypography variant='Small_Medium' mr='8px'>
								Remove
							</LencoTypography>
							x
						</FlexBox>
					</LencoButton>
				</FlexBox>
			)}
		</>
	);
}
